import { createContext, useContext } from 'react';

// Type for a registry entry
type Entry = {
  element: HTMLElement;
};

// Type for card registry
type CardRegistry = {
  [id: string]: Entry;
};

// Type for column registry
type ColumnRegistry = {
  [id: string]: Entry;
};

// Type for board context
export type BoardContextValue = {
  instanceId: symbol;
  getColumns: () => Array<{
    id: string;
    title: string;
    taskIds: string[];
  }>;
  reorderTask: (args: {
    columnId: string;
    startIndex: number;
    finishIndex: number;
    trigger?: 'pointer' | 'keyboard';
  }) => void;
  moveTask: (args: {
    startColumnId: string;
    finishColumnId: string;
    taskIndexInStartColumn: number;
    taskIndexInFinishColumn?: number;
    trigger?: 'pointer' | 'keyboard';
  }) => void;
  registerCard: (args: {
    taskId: string;
    entry: Entry;
  }) => () => void;
  registerColumn: (args: {
    columnId: string;
    entry: Entry;
  }) => () => void;
};

// Create registry for keeping track of drag/drop elements
export function createRegistry() {
  const cardRegistry: CardRegistry = {};
  const columnRegistry: ColumnRegistry = {};

  function registerCard({ taskId, entry }: { taskId: string; entry: Entry }) {
    cardRegistry[taskId] = entry;
    return () => {
      delete cardRegistry[taskId];
    };
  }

  function registerColumn({ columnId, entry }: { columnId: string; entry: Entry }) {
    columnRegistry[columnId] = entry;
    return () => {
      delete columnRegistry[columnId];
    };
  }

  function getCard(cardId: string): Entry | undefined {
    return cardRegistry[cardId];
  }

  function getColumn(columnId: string): Entry | undefined {
    return columnRegistry[columnId];
  }

  return {
    registerCard,
    registerColumn,
    getCard,
    getColumn,
  };
}

// Create the context with a fallback
export const BoardContext = createContext<BoardContextValue | null>(null);

// Custom hook for using board context
export function useBoardContext(): BoardContextValue {
  const context = useContext(BoardContext);
  
  if (!context) {
    throw new Error('useBoardContext must be used within a BoardContext.Provider');
  }
  
  return context;
}